// src/pages/Home.tsx

import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { storeAfid } from '../utils/afidHandler';
import ParticleBackground from '../components/layout/ParticleBackground';
import ThemeToggle from '../components/ui/ThemeToggle';
import { useTheme } from '../contexts/ThemeContext';

const HomeContainer = styled.div`
  background-color: transparent;
  color: var(--color-text);
  padding: 2rem 0;
  user-select: none;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 0;
  }
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
  text-align: center;
  position: relative;
  z-index: 1;
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideDownFadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideUpFadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0% {
    transform: scale(1);
    opacity: 0.7;
  }
  50% {
    transform: scale(1.05);
    opacity: 1;
  }
  100% {
    transform: scale(1);
    opacity: 0.7;
  }
`;

const fadeOut = keyframes`
  from { opacity: 1; }
  to { opacity: 0; }
`;

const Title = styled.h1`
  color: var(--color-text);
  font-size: 4.5rem;
  margin-bottom: 1.5rem;
  font-weight: 700;
  letter-spacing: 2px;
  text-transform: uppercase;
  position: relative;
  animation: ${fadeIn} 1s ease-out forwards;
  opacity: 0;
  font-family: 'Maragsa-Display', Helvetica, Arial, sans-serif;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 100px;
    height: 3px;
    background: linear-gradient(90deg, transparent, var(--color-primary), transparent);
  }

  @media (max-width: 768px) {
    font-size: 2.8rem;
    letter-spacing: 1px;
  }
`;

const RiskMasterTitle = styled(Title)`
  animation: ${slideDownFadeIn} 0.6s ease-out forwards;
  animation-delay: 0.2s;
  opacity: 0;
  position: relative;
  display: inline-block;
`;

const Subtitle = styled.h2`
  color: var(--color-text);
  font-size: 2rem;
  margin-bottom: 3rem;
  font-weight: 300;
  letter-spacing: 1px;
  animation: ${slideUpFadeIn} 0.6s ease-out 0.5s forwards;
  opacity: 0;
  line-height: 1.4;
  font-family: 'Maragsa-Display', Helvetica, Arial, sans-serif;

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

interface CtaButtonProps {
  theme: 'light' | 'dark';
}

const CtaButton = styled.button<CtaButtonProps>`
  background: transparent;
  color: var(--color-primary);
  border: 2px solid var(--color-primary);
  padding: 1rem 2.5rem;
  font-size: 1.2rem;
  font-weight: 500;
  border-radius: 50px;
  cursor: pointer;
  transition: all 0.3s ease;
  animation: ${slideUpFadeIn} 0.6s ease-out 0.8s forwards;
  opacity: 0;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s ease;
  }
  
  &:hover {
    background: var(--color-primary);
    color: ${props => props.theme === 'dark' ? '#000000' : '#ffffff'};
    transform: translateY(-2px);
    box-shadow: 0 10px 25px rgba(255, 255, 255, 0.2);
    
    &::before {
      left: 100%;
    }
  }
  
  &:active {
    transform: translateY(0);
  }
`;

interface ScrollIndicatorProps {
  isScrolled: boolean;
}

const ScrollIndicator = styled.div<ScrollIndicatorProps>`
  position: fixed;
  bottom: 20px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  animation: ${props => props.isScrolled ? fadeOut : pulse} 0.5s forwards;
  z-index: 1000; /* Ensure it's above other content */
  
  &::after {
    content: '↓';
    font-size: 2rem;
    color: var(--color-text);
    opacity: 0.6;
  }
`;

const Home: React.FC = () => {
  const location = useLocation();
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { theme } = useTheme();

  useEffect(() => {
    // Extract the 'afid' from the query parameters
    const params = new URLSearchParams(location.search);
    const afid = params.get('afid');

    if (afid) {
      // Store the afid using the centralized handler
      storeAfid(afid);
    }
  
    if (window.fbq) {
      window.fbq('track', 'ViewContent', { page: 'Home' });
    }

    // Show scroll indicator after animations complete
    const timer = setTimeout(() => {
      setShowScrollIndicator(true);
    }, 1000);

    // Add scroll event listener to fade out arrow when scrolling down
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    
    return () => {
      clearTimeout(timer);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [location.search]);

  const handleGetStarted = () => {
    // Scroll to features section or navigate to download page
    const featuresSection = document.getElementById('features');
    if (featuresSection) {
      featuresSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <HomeContainer>
      <Helmet>
        <title>Risk Master - Elevate Your Trading Strategy</title>
        <meta
          name="description"
          content="Enhance your trading strategy with Risk Master. Manage risks with personalized profiles and automated safeguards to achieve consistent profits."
        />
        <link rel="canonical" href="https://www.riskmaster.net/" />
      </Helmet>
      <ParticleBackground />
      <ContentWrapper>
        <ThemeToggle />
        <RiskMasterTitle>
          Risk Master
        </RiskMasterTitle>
        <Subtitle>Where discipline meets profit</Subtitle>
        <CtaButton theme={theme} onClick={handleGetStarted}>
          Get Started
        </CtaButton>
      </ContentWrapper>
      {showScrollIndicator && <ScrollIndicator isScrolled={isScrolled} />}
    </HomeContainer>
  );
};

export default Home;
